import {
  Certificate,
  Crown,
  Microphone,
  ChalkboardTeacher,
  Lightning,
  BookOpenText,
  ArrowSquareOut,
  GraduationCap,
} from "@phosphor-icons/react";

const KIND_ICON = {
  Reviewing: Certificate,
  Training: GraduationCap,
  "Book Editor": BookOpenText,
  "Track Chair": Crown,
  "Session Chair": ChalkboardTeacher,
  Panellist: Microphone,
  "Paper Presentation": Lightning,
};

const COLOR_CLASS = {
  terracotta: {
    stripe: "bg-terracotta",
    icon: "text-terracotta",
    iconBg: "bg-terracotta/10 border-terracotta",
    badge: "badge-terracotta",
    seal: "border-terracotta text-terracotta",
  },
  saffron: {
    stripe: "bg-saffron",
    icon: "text-saffron-hover",
    iconBg: "bg-saffron/15 border-saffron",
    badge: "badge-saffron",
    seal: "border-saffron text-saffron-hover",
  },
  teal: {
    stripe: "bg-teal",
    icon: "text-teal",
    iconBg: "bg-teal/10 border-teal",
    badge: "badge-teal",
    seal: "border-teal text-teal",
  },
  indigo: {
    stripe: "bg-indigo",
    icon: "text-indigo",
    iconBg: "bg-indigo/10 border-indigo",
    badge: "badge-indigo",
    seal: "border-indigo text-indigo",
  },
};

function CertificateCard({ cert, idx }) {
  const Icon = KIND_ICON[cert.kind] || Certificate;
  const palette = COLOR_CLASS[cert.color] || COLOR_CLASS.terracotta;
  const Wrapper = cert.url ? "a" : "div";
  const wrapperProps = cert.url
    ? { href: cert.url, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="editorial-card p-6 lg:p-7 relative overflow-hidden group bg-white avoid-break"
      data-testid={`certificate-card-${idx}`}
    >
      <div className={`absolute top-0 left-0 h-1 w-full ${palette.stripe}`} />
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className={`w-11 h-11 ${palette.iconBg} border flex items-center justify-center shrink-0`}>
          <Icon size={22} weight="duotone" className={palette.icon} />
        </div>
        <div className={`pill-color ${palette.badge} text-[10px] py-1`}>{cert.kind}</div>
      </div>

      <h3 className="font-display text-xl lg:text-2xl tracking-tight leading-tight mb-3 group-hover:text-terracotta transition-colors">
        {cert.title}
      </h3>

      <div className="text-sm text-ink-mute leading-snug mb-4">{cert.issuer}</div>

      <div className="flex items-end justify-between gap-3 pt-3 border-t border-paper-edge">
        <div className={`font-mono text-xs px-2.5 py-1 border ${palette.seal} bg-paper-alt`}>
          {cert.date}
        </div>
        {cert.isbn && (
          <div className="font-mono text-[10px] text-ink-mute uppercase tracking-wider">
            ISBN {cert.isbn}
          </div>
        )}
        {cert.url && (
          <ArrowSquareOut size={14} weight="bold" className={`${palette.icon} opacity-60 group-hover:opacity-100`} />
        )}
      </div>
    </Wrapper>
  );
}

export const Credentials = ({ data }) => {
  if (!data) return null;
  const certificates = data.certificates || [];

  return (
    <div data-testid="credentials-section" className="space-y-8">
      {certificates.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
          data-testid="certificates-grid"
        >
          {certificates.map((c, i) => (
            <CertificateCard key={`${c.kind}-${c.title}-${c.date}`} cert={c} idx={i} />
          ))}
        </div>
      )}

      {data.indexed_chapters_highlight?.length > 0 && (
        <div
          className="editorial-card p-6 lg:p-8 bg-paper-alt avoid-break"
          data-testid="chapters-highlight"
        >
          <div className="overline text-indigo mb-3">Selected Indexed Book Chapters</div>
          <ul className="space-y-2 font-display text-lg leading-snug">
            {data.indexed_chapters_highlight.map((c) => (
              <li key={c} className="flex items-start gap-3">
                <span className="text-terracotta font-mono text-xs mt-1.5 shrink-0">▸</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Credentials;
